// packages/web/src/components/admin/email-templates/template-preview-pane.tsx
import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { usePreviewEmailTemplate } from "@/api/email-templates/mutations";
import { SendTestDialog } from "./send-test-dialog";

interface Props {
  templateKey: string;
  locale: string;
  draft: { subject: string; slots: Record<string, string> };
  sampleVars: Record<string, unknown>;
}

type Tab = "html" | "text" | "meta";

interface Rendered {
  resolvedSubject: string;
  html: string;
  text: string;
  from: string;
  replyTo: string | null;
}

function substituteSimple(source: string, vars: Record<string, unknown>): string {
  return source.replace(/\{\{\s*([a-zA-Z_]\w*)\s*\}\}/g, (_m, key: string) =>
    Object.prototype.hasOwnProperty.call(vars, key) ? String(vars[key]) : `{{${key}}}`,
  );
}

const TAB_LABELS: Record<Tab, string> = {
  html: "HTML",
  text: "Tekst",
  meta: "Metadata",
};

export function TemplatePreviewPane({ templateKey, locale, draft, sampleVars }: Props) {
  const [tab, setTab] = useState<Tab>("html");
  const [rendered, setRendered] = useState<Rendered | null>(null);
  const [sendTestOpen, setSendTestOpen] = useState(false);
  const previewMutation = usePreviewEmailTemplate();

  const liveText =
    substituteSimple(draft.subject, sampleVars) +
    "\n\n" +
    Object.entries(draft.slots)
      .map(([, v]) => substituteSimple(v, sampleVars))
      .join("\n\n");

  async function runServerPreview() {
    try {
      const result = await previewMutation.mutateAsync({
        key: templateKey,
        body: {
          locale,
          subject: draft.subject,
          slots: draft.slots,
          sampleVars,
        },
      });
      setRendered(result);
    } catch (err) {
      toast.error((err as Error).message);
    }
  }

  return (
    <div className="flex flex-col border border-line rounded-lg bg-surface">
      <div className="flex items-center gap-1 px-3 pt-3 pb-2 border-b border-line flex-wrap">
        {(["html", "text", "meta"] as const).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={`px-3 py-1 text-sm rounded ${
              tab === t
                ? "bg-p-100 text-p-700 font-medium"
                : "text-ink-500 hover:text-ink-700 hover:bg-surface-1"
            }`}
          >
            {TAB_LABELS[t]}
          </button>
        ))}
        <div className="ml-auto flex gap-2">
          <Button
            type="button"
            variant="default"
            size="compact"
            onClick={() => void runServerPreview()}
            disabled={previewMutation.isPending}
          >
            Voorbeeld renderen
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="compact"
            onClick={() => setSendTestOpen(true)}
          >
            Testmail sturen
          </Button>
        </div>
      </div>

      <div className="p-4 flex-1">
        {tab === "html" &&
          (rendered ? (
            <iframe
              title="Email HTML preview"
              sandbox=""
              srcDoc={rendered.html}
              className="w-full h-[500px] border border-line rounded"
            />
          ) : (
            <p className="text-sm text-ink-500">
              Klik op &ldquo;Voorbeeld renderen&rdquo; om de definitieve HTML-weergave te zien.
            </p>
          ))}

        {tab === "text" && (
          <pre className="text-xs whitespace-pre-wrap text-ink-700 max-h-[500px] overflow-auto">
            {rendered ? rendered.text : liveText}
          </pre>
        )}

        {tab === "meta" &&
          (rendered ? (
            <dl className="text-sm space-y-2">
              <div>
                <dt className="inline text-ink-500">Onderwerp: </dt>
                <dd className="inline">{rendered.resolvedSubject}</dd>
              </div>
              <div>
                <dt className="inline text-ink-500">Van: </dt>
                <dd className="inline font-mono">{rendered.from}</dd>
              </div>
              <div>
                <dt className="inline text-ink-500">Antwoorden naar: </dt>
                <dd className="inline font-mono">{rendered.replyTo ?? "(geen)"}</dd>
              </div>
            </dl>
          ) : (
            <p className="text-sm text-ink-500">
              Klik op &ldquo;Voorbeeld renderen&rdquo; om metadata te bekijken.
            </p>
          ))}
      </div>

      <SendTestDialog
        open={sendTestOpen}
        onOpenChange={setSendTestOpen}
        templateKey={templateKey}
        locale={locale}
        draft={draft}
        sampleVars={sampleVars}
      />
    </div>
  );
}
